import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Bell,
  Bus,
  LayoutDashboard,
  LogOut,
  MapPinned,
  Settings,
  School,
  ShieldCheck,
  UserRound,
  Users,
  ClipboardList,
  Route,
  FileBarChart2,
} from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../contexts/AuthContext";
import { cn, getRoleLabel } from "../lib/utils";

const navByRole = {
  admin: [
    { label: "Dashboard", icon: LayoutDashboard, to: "/admin", end: true },
    { label: "Trips", icon: Route, to: "/admin/trips" },
    { label: "Vehicles", icon: Bus, to: "/admin/vehicles" },
    { label: "Incidents", icon: AlertTriangle, to: "/admin/incidents" },
    { label: "Users", icon: Users, to: "/admin/users" },
    { label: "Schools", icon: School, to: "/admin/schools" },
    { label: "Parents", icon: UserRound, to: "/admin/parents" },
    { label: "Reports", icon: FileBarChart2, to: "/admin/reports" },
    { label: "Notifications", icon: Bell, to: "/admin/notifications" },
    { label: "Settings", icon: Settings, to: "/admin/settings" },
  ],
  driver: [
    { label: "Dashboard", icon: LayoutDashboard, to: "/driver", end: true },
    { label: "Trips", icon: Route, to: "/driver/trips" },
    { label: "Incidents", icon: AlertTriangle, to: "/driver/incidents" },
    { label: "Notifications", icon: Bell, to: "/driver/notifications" },
    { label: "Settings", icon: Settings, to: "/driver/settings" },
  ],
  school: [
    { label: "Dashboard", icon: LayoutDashboard, to: "/school", end: true },
    { label: "Trips", icon: ClipboardList, to: "/school/trips" },
    { label: "Incidents", icon: AlertTriangle, to: "/school/incidents" },
    { label: "Notifications", icon: Bell, to: "/school/notifications" },
    { label: "Settings", icon: Settings, to: "/school/settings" },
  ],
  parent: [
    { label: "Dashboard", icon: MapPinned, to: "/parent", end: true },
    { label: "Notifications", icon: Bell, to: "/parent/notifications" },
    { label: "Settings", icon: Settings, to: "/parent/settings" },
  ],
};

export const Sidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const links = navByRole[user?.role] || navByRole.admin;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <>
      {isOpen && <div className="fixed inset-0 z-30 bg-slate-950/40 lg:hidden" onClick={onClose} />}
      <aside className={cn("app-sidebar", isOpen && "open")}>
        <div className="border-b border-[#E2E8F0] p-6">
          <Logo />
          <div className="mt-4 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#163D6B] text-white">
                <ShieldCheck size={18} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[#0F172A]">{user?.name}</p>
                <p className="truncate text-xs text-[#64748B]">{getRoleLabel(user?.role)}</p>
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-4">
          {links.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  "nav-item rounded-xl px-4 py-3",
                  isActive && "active"
                )
              }
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-[#E2E8F0] p-4">
          <button
            onClick={handleLogout}
            className="nav-item w-full rounded-xl px-4 py-3 text-[#DC2626] hover:bg-[#FEF2F2]"
          >
            <LogOut size={18} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
